import { useState, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist';

const ComparativeChartView = ({
  filteredData,
  filteredData2,
  selectedVariable,
  selectedMeasure,
  selectedVariable2,
  selectedMeasure2,
  dataStructure,
  chartXAxis,
  showLegend,
  isSidebarOpen,
  isRightPanelOpen,
  variableDefinitions
}) => {
  const plotlyChartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 800, height: 600 });
  const [title1Width, setTitle1Width] = useState(0);
  const [title2Width, setTitle2Width] = useState(0);
  
  // Debounce utility function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Font loading utility (copied from ChartView)
  const ensureFontLoaded = () => {
    return new Promise((resolve) => {
      if (document.fonts && document.fonts.check) {
        if (document.fonts.check('300 16px Calibre-Light')) {
          resolve();
        } else {
          document.fonts.ready.then(() => {
            setTimeout(resolve, 100); // Small delay to ensure font is fully loaded
          });
        }
      } else {
        // Fallback for browsers without font loading API
        setTimeout(resolve, 500);
      }
    });
  };

  // Custom function to format numbers for y-axis ticks (copied from ChartView)
  const formatYAxisTick = (value) => {
    if (value === 0) return '0';
    
    const absValue = Math.abs(value);
    
    if (absValue >= 1000000000) {
      // Billions
      const billions = value / 1000000000;
      if (billions === Math.floor(billions)) {
        return billions + 'B';
      } else {
        return billions.toFixed(1) + 'B';
      }
    } else if (absValue >= 1000000) {
      // Millions
      const millions = value / 1000000;
      if (millions === Math.floor(millions)) {
        return millions + 'M';
      } else {
        return millions.toFixed(1) + 'M';
      }
    } else if (absValue >= 1000) {
      // Thousands
      const thousands = value / 1000;
      if (thousands === Math.floor(thousands)) {
        return thousands + 'k';
      } else {
        return thousands.toFixed(1) + 'k';
      }
    } else {
      // Less than 1000
      if (value === Math.floor(value)) {
        return value.toString();
      } else {
        return value.toFixed(1);
      }
    }
  };

  // Function to generate "nice" tick values (copied from ChartView)
  const generateNiceTicks = (minValue, maxValue, targetTickCount = 6) => {
    if (minValue === maxValue) {
      return [minValue];
    }

    const range = maxValue - minValue;
    const roughStep = range / (targetTickCount - 1);
    
    // Find the order of magnitude
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    
    // Normalize the rough step to get a nice step size
    const normalizedStep = roughStep / magnitude;
    let niceStep;
    
    if (normalizedStep <= 1) {
      niceStep = 1;
    } else if (normalizedStep <= 2) {
      niceStep = 2;
    } else if (normalizedStep <= 5) {
      niceStep = 5;
    } else {
      niceStep = 10;
    }
    
    const stepSize = niceStep * magnitude;
    
    // Find nice min and max values
    const niceMin = Math.floor(minValue / stepSize) * stepSize;
    const niceMax = Math.ceil(maxValue / stepSize) * stepSize;
    
    // Generate tick values
    const ticks = [];
    for (let tick = niceMin; tick <= niceMax + stepSize * 0.1; tick += stepSize) {
      // Round to avoid floating point precision issues
      const roundedTick = Math.round(tick / magnitude) * magnitude;
      ticks.push(roundedTick);
    }
    
    return ticks;
  };

  // Calculate available space more accurately - Updated for full height only
  const calculateDimensions = () => {
    if (!chartContainerRef.current) return { width: 800, height: 600 };

    // Get viewport height for full screen extension
    const viewportHeight = window.innerHeight;
    
    // Get the actual container dimensions for width (respects parent layout)
    const container = chartContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate available width using container (respects sidebars)
    // Calculate available height using viewport (extends to bottom)
    const horizontalPadding = 10; // Minimal horizontal padding for full width
    const verticalPadding = 20; // Top padding
    const bottomWatermarkSpace = 120; // Increased space for copyright and logo watermarks to prevent cutoff
    
    // Use container width (respects existing layout) but viewport height
    const topOffset = containerRect.top; // Distance from top of viewport
    const availableHeight = Math.max(viewportHeight - topOffset - bottomWatermarkSpace, 500);
    const availableWidth = Math.max(containerRect.width - horizontalPadding, 300);

    console.log('ComparativeChartView: Calculating dimensions (height extension):', {
      viewportHeight,
      containerRect,
      topOffset,
      availableWidth,
      availableHeight,
      isSidebarOpen,
      isRightPanelOpen
    });

    return {
      width: availableWidth,
      height: availableHeight
    };
  };

  // Utility function to truncate text for title (no wrapping)
  const truncateTitle = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  // Handle resize for window and sidebar/panel changes
  useEffect(() => {
    const handleResize = () => {
      const newDimensions = calculateDimensions();
      setChartDimensions(newDimensions);

      // Resize Plotly chart if it exists with safety checks
      if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout) {
        setTimeout(() => {
          // Double-check the ref still exists after timeout
          if (plotlyChartRef.current && plotlyChartRef.current._fullLayout) {
            try {
              Plotly.Plots.resize(plotlyChartRef.current);
            } catch (error) {
              console.warn('ComparativeChartView: Plotly resize failed (element may have been unmounted):', error);
            }
          }
        }, 100);
      }

      console.log('ComparativeChartView: Resizing chart to:', newDimensions);
    };

    const debouncedResize = debounce(handleResize, 150);
    
    // Initial calculation
    setTimeout(handleResize, 100);
    
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [isSidebarOpen, isRightPanelOpen]);

  // Recalculate dimensions when sidebar or panel state changes
  useEffect(() => {
    setTimeout(() => {
      const newDimensions = calculateDimensions();
      setChartDimensions(newDimensions);
      
      if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout) {
        setTimeout(() => {
          // Safety check before resize
          if (plotlyChartRef.current && plotlyChartRef.current._fullLayout) {
            try {
              Plotly.Plots.resize(plotlyChartRef.current);
            } catch (error) {
              console.warn('ComparativeChartView: Plotly resize failed during state change:', error);
            }
          }
        }, 200);
      }
    }, 300); // Wait for CSS transitions to complete
  }, [isSidebarOpen, isRightPanelOpen]);

  // Custom download function with watermarks (copied and adapted from ChartView)
  const downloadChartWithWatermark = async () => {
    if (!plotlyChartRef.current || !plotlyChartRef.current._fullLayout) return;
    
    try {
      // Ensure font is loaded before downloading
      await ensureFontLoaded();
      
      // Get current layout and data from the chart
      const currentLayout = plotlyChartRef.current.layout;
      const currentData = plotlyChartRef.current.data;
      
      // IMPORTANT: Force a fresh render before downloading to ensure all data is properly rendered
      console.log('Pre-download data check:', {
        totalTraces: currentData.length,
        subplot1Traces: currentData.filter(trace => !trace.yaxis || trace.yaxis === 'y').length,
        subplot2Traces: currentData.filter(trace => trace.yaxis === 'y2').length,
        dataIntegrity: currentData.map((trace, i) => ({
          index: i,
          name: trace.name,
          dataPoints: trace.x ? trace.x.length : 0,
          yAxis: trace.yaxis || 'y',
          visible: trace.visible !== false
        }))
      });
      
      // Create layout with watermarks positioned lower
      const watermarkedLayout = {
        ...currentLayout,
        images: [
          {
            source: '/logo.png',
            xref: 'paper',
            yref: 'paper',
            x: 0.98,
            y: -0.12, // Moved much lower to avoid overlapping with chart
            sizex: 0.12,
            sizey: 0.12,
            xanchor: 'right',
            yanchor: 'bottom',
            opacity: 0.9,
            layer: 'above'
          }
        ],
        annotations: [
          ...(currentLayout.annotations || []),
          {
            text: '© Health Systems Innovation Lab, Harvard University, 2025',
            xref: 'paper',
            yref: 'paper',
            x: 0.01,
            y: -0.12, // Moved much lower to avoid overlapping with chart
            xanchor: 'left',
            yanchor: 'bottom',
            showarrow: false,
            font: {
              size: 11,
              color: 'rgba(0, 0, 0, 0.9)',
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            bgcolor: 'rgba(255, 255, 255, 0.7)', // Lighter background
            borderwidth: 0, // No border
            borderpad: 6
          }
        ],
        // Ensure global font is applied to the watermarked version too
        font: {
          family: 'Calibre-Light, Arial, sans-serif',
          size: 14
        }
      };
      
      // CRITICAL: Re-render with fresh data before download to prevent data loss
      await Plotly.react(plotlyChartRef.current, currentData, watermarkedLayout);
      
      // Wait a moment for the render to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Download the image
      await Plotly.downloadImage(plotlyChartRef.current, {
        format: 'png',
        filename: `comparative_chart_${selectedVariable || 'data1'}_vs_${selectedVariable2 || 'data2'}`,
        height: chartDimensions.height,
        width: chartDimensions.width,
        scale: 2
      });
      
      // Revert to original layout without watermarks
      await Plotly.react(plotlyChartRef.current, currentData, currentLayout);
      
    } catch (error) {
      console.error('Error downloading comparative chart with watermark:', error);
    }
  };

  // Create and update Plotly visualization
  useEffect(() => {
    if (!plotlyChartRef.current || !window.Plotly) {
      console.warn('Plotly chart ref or Plotly library not available');
      return;
    }

    console.log('ComparativeChartView rendering with data:', {
      filteredDataLength: filteredData?.length || 0,
      filteredData2Length: filteredData2?.length || 0,
      selectedVariable,
      selectedMeasure,
      selectedVariable2,
      selectedMeasure2,
      chartXAxis,
      showLegend
    });

    if ((!filteredData || filteredData.length === 0) && (!filteredData2 || filteredData2.length === 0)) {
      console.log('No data available for comparative chart');
      return;
    }

    // Ensure font is loaded before creating chart
    ensureFontLoaded().then(() => {
      // Capture current title widths at the start of the effect
      const currentTitle1Width = title1Width;
      const currentTitle2Width = title2Width;
      
      // Create consistent colors for countries across both charts
      const allCountries = [...new Set([
        ...(filteredData || []).map(row => row.Country),
        ...(filteredData2 || []).map(row => row.Country)
      ])];
      
      const colors = [
        '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
        '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5', '#c49c94', '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5'
      ];
      
      const countryColors = {};
      allCountries.forEach((country, index) => {
        countryColors[country] = colors[index % colors.length];
      });

      const prepareChartData = (data, variable, measure, subplotIndex) => {
        if (!data || data.length === 0) {
          console.log(`No data provided for subplot ${subplotIndex}`);
          return [];
        }

        console.log(`Preparing chart data for subplot ${subplotIndex}:`, {
          dataLength: data.length,
          variable,
          measure,
          chartXAxis,
          sampleData: data.slice(0, 2)
        });

        if (chartXAxis === 'Year') {
          const countryGroups = {};
          data.forEach(row => {
            if (!countryGroups[row.Country]) {
              countryGroups[row.Country] = {
                x: [],
                y: [],
                name: row.Country
              };
            }
            countryGroups[row.Country].x.push(row.Year);
            countryGroups[row.Country].y.push(row.Value);
          });
          
          const result = Object.values(countryGroups).map(group => {
            const color = countryColors[group.name] || '#1f77b4';
            return {
              x: group.x,
              y: group.y,
              mode: 'lines+markers',
              type: 'scatter',
              name: group.name, // Remove subplot identifier from trace names
              hovertemplate: `<b>%{fullData.name}</b><br>Year: %{x}<br>${measure || 'Value'}: %{y:,.0f}<extra></extra>`,
              xaxis: `x${subplotIndex === 1 ? '' : '2'}`,
              yaxis: `y${subplotIndex === 1 ? '' : '2'}`,
              marker: {
                size: 8,
                opacity: 0.8,
                color: color
              },
              line: {
                width: 2,
                color: color
              },
              showlegend: true,
              // FIXED: Assign traces to different legends based on subplot
              legend: subplotIndex === 1 ? 'legend' : 'legend2',
              // ADD: Custom hover styling with color-coded background
              hoverlabel: {
                bgcolor: color,
                bordercolor: 'white',
                font: {
                  color: 'white',
                  size: 12,
                  family: 'Calibre-Light, Arial, sans-serif'
                }
              }
            };
          });
          
          console.log(`Year-based chart data for subplot ${subplotIndex}:`, {
            resultLength: result.length,
            sampleResult: result[0] ? {
              name: result[0].name,
              xLength: result[0].x.length,
              yLength: result[0].y.length,
              color: result[0].marker.color,
              legendgroup: result[0].legendgroup
            } : 'No result'
          });
          
          return result;
        } else {
          // Group by country for scatter plots too
          const countryGroups = {};
          data.forEach(row => {
            if (!countryGroups[row.Country]) {
              countryGroups[row.Country] = {
                x: [],
                y: [],
                hoverTexts: [],
                name: row.Country
              };
            }
            
            let xValue, yValue;
            if (chartXAxis === 'Value') {
              xValue = row.Value;
            } else {
              xValue = row[chartXAxis];
            }
            yValue = row.Value;
            
            if (xValue !== undefined && yValue !== undefined) {
              countryGroups[row.Country].x.push(xValue);
              countryGroups[row.Country].y.push(yValue);
              const formattedX = typeof xValue === 'number' ? xValue.toLocaleString() : xValue;
              const formattedY = typeof yValue === 'number' ? yValue.toLocaleString() : yValue;
              countryGroups[row.Country].hoverTexts.push(`<b>${row.Country}</b><br>X: ${formattedX}<br>Y: ${formattedY}`);
            }
          });
          
          const result = Object.values(countryGroups).map(group => {
            const color = countryColors[group.name] || '#1f77b4';
            return {
              x: group.x,
              y: group.y,
              text: group.hoverTexts,
              mode: 'markers',
              type: 'scatter',
              name: group.name, // Remove subplot identifier from trace names
              hovertemplate: '%{text}<extra></extra>',
              marker: {
                size: 10,
                color: color,
                opacity: 0.7
              },
              xaxis: `x${subplotIndex === 1 ? '' : '2'}`,
              yaxis: `y${subplotIndex === 1 ? '' : '2'}`,
              showlegend: true,
              // FIXED: Assign traces to different legends based on subplot
              legend: subplotIndex === 1 ? 'legend' : 'legend2',
              // ADD: Custom hover styling with color-coded background
              hoverlabel: {
                bgcolor: color,
                bordercolor: 'white',
                font: {
                  color: 'white',
                  size: 12,
                  family: 'Calibre-Light, Arial, sans-serif'
                }
              }
            };
          });
          
          console.log(`Scatter chart data for subplot ${subplotIndex}:`, {
            resultLength: result.length,
            sampleResult: result[0] ? {
              name: result[0].name,
              xLength: result[0].x.length,
              yLength: result[0].y.length,
              color: result[0].marker.color,
              legendgroup: result[0].legendgroup
            } : 'No result'
          });
          
          return result;
        }
      };

      const plotlyData1 = prepareChartData(filteredData, selectedVariable, selectedMeasure, 1);
      const plotlyData2 = prepareChartData(filteredData2, selectedVariable2, selectedMeasure2, 2);
      const plotlyDataArray = [...plotlyData1, ...plotlyData2];

      console.log('Final plotly data:', {
        plotlyData1Length: plotlyData1.length,
        plotlyData2Length: plotlyData2.length,
        totalDataLength: plotlyDataArray.length,
        allCountries,
        countryColors,
        // Additional debugging for data integrity
        dataIntegrity: plotlyDataArray.map((trace, i) => ({
          index: i,
          name: trace.name,
          legendgroup: trace.legendgroup,
          xAxis: trace.xaxis,
          yAxis: trace.yaxis,
          dataPoints: trace.x ? trace.x.length : 0
        }))
      });

      // Determine if we should show legend based on number of unique countries
      const shouldShowLegend = allCountries.length > 1;

      // Extract unique years for tick labels when chartXAxis is 'Year'
      let tickvals = [];
      let ticktext = [];
      if (chartXAxis === 'Year') {
        const allYears = [
          ...new Set([
            ...(filteredData || []).map(row => row.Year),
            ...(filteredData2 || []).map(row => row.Year)
          ].filter(y => y !== undefined))
        ].sort((a, b) => a - b);
        tickvals = allYears;
        ticktext = allYears.map(year => String(year));
      }

      // Calculate y-axis range and generate nice tick values for first subplot
      const allYValues1 = plotlyData1.flatMap(trace => trace.y || []);
      let customTickVals1 = [];
      let customTickText1 = [];
      if (allYValues1.length > 0) {
        const minY1 = Math.min(...allYValues1);
        const maxY1 = Math.max(...allYValues1);
        customTickVals1 = generateNiceTicks(minY1, maxY1, 6);
        customTickText1 = customTickVals1.map(val => formatYAxisTick(val));
      }

      // Calculate y-axis range and generate nice tick values for second subplot
      const allYValues2 = plotlyData2.flatMap(trace => trace.y || []);
      let customTickVals2 = [];
      let customTickText2 = [];
      if (allYValues2.length > 0) {
        const minY2 = Math.min(...allYValues2);
        const maxY2 = Math.max(...allYValues2);
        customTickVals2 = generateNiceTicks(minY2, maxY2, 6);
        customTickText2 = customTickVals2.map(val => formatYAxisTick(val));
      }

      // Define title annotations with tooltips - NO WRAPPING
      const annotations = [
        // Title for first subplot
        {
          text: truncateTitle(selectedVariable || 'Value', 60),
          xref: "paper",
          yref: "paper",
          x: 0.5,
          y: 0.98, // Moved closer to top
          showarrow: false,
          font: {
            size: 18, // Slightly smaller to fit better
            family: 'Calibre-Light, Arial, sans-serif',
            weight: 'bold',
            color: "black"
          },
          xanchor: 'center',
          yanchor: 'top'
        },
        // Title for second subplot
        {
          text: truncateTitle(selectedVariable2 || 'Value', 60),
          xref: "paper",
          yref: "paper",
          x: 0.5,
          y: 0.48, // Moved closer to middle
          showarrow: false,
          font: {
            size: 18, // Slightly smaller to fit better
            family: 'Calibre-Light, Arial, sans-serif',
            weight: 'bold',
            color: "black"
          },
          xanchor: 'center',
          yanchor: 'top'
        },
        // Tooltip for first subplot title
        {
          text: "ⓘ",
          xref: "paper",
          yref: "paper",
          x: 0.95,
          y: 0.98,
          showarrow: false,
          font: {
            size: 14, // Smaller icon
            color: "#3B82F6"
          },
          bgcolor: "rgba(255,255,255,0.8)",
          borderwidth: 0,
          hovertext: selectedMeasure || '',
          hoverlabel: {
            bgcolor: "white",
            bordercolor: "#3B82F6",
            font: {size: 12, color: "black"},
            align: "left",
            xanchor: "right"
          }
        },
        // Tooltip for second subplot title
        {
          text: "ⓘ",
          xref: "paper",
          yref: "paper",
          x: 0.95,
          y: 0.48,
          showarrow: false,
          font: {
            size: 14, // Smaller icon
            color: "#3B82F6"
          },
          bgcolor: "rgba(255,255,255,0.8)",
          borderwidth: 0,
          hovertext: selectedMeasure2 || '',
          hoverlabel: {
            bgcolor: "white",
            bordercolor: "#3B82F6",
            font: {size: 12, color: "black"},
            align: "left",
            xanchor: "right"
          }
        }
      ];

      const layout = {
        grid: { rows: 2, columns: 1, pattern: 'independent' },
        width: chartDimensions.width,
        height: chartDimensions.height,
        annotations: annotations,
        xaxis: {
          title: {
            text: '',
            font: {
              size: 18,
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            }
          },
          tickfont: {
            size: 14,
            family: 'Calibre-Light, Arial, sans-serif'
          },
          tickformat: ',',
          hoverformat: ',.0f',
          domain: [0, 1],
          anchor: 'y',
          ...(chartXAxis === 'Year' && {
            tickmode: 'array',
            tickvals: tickvals,
            ticktext: ticktext,
            tickangle: 45,
            range: tickvals.length > 0 ? [Math.min(...tickvals) - 0.5, Math.max(...tickvals) + 0.5] : undefined
          })
        },
        yaxis: {
          title: {
            text: '',
            font: {
              size: 18,
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            standoff: 30,
            automargin: true
          },
          tickfont: {
            size: 13,
            family: 'Calibre-Light, Arial, sans-serif'
          },
          // Use custom tick values and text for first subplot
          tickmode: customTickVals1.length > 0 ? 'array' : 'auto',
          tickvals: customTickVals1.length > 0 ? customTickVals1 : undefined,
          ticktext: customTickText1.length > 0 ? customTickText1 : undefined,
          hoverformat: ',.0f', // Keep full numbers in hover tooltips
          anchor: 'x',
          domain: [0.58, 0.95], // More space below title, smaller top chart
          side: 'left',
          tickangle: 0,
          automargin: true
        },
        xaxis2: {
          title: {
            text: chartXAxis === 'Year' ? 'Year' : chartXAxis,
            font: {
              size: 18,
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            }
          },
          tickfont: {
            size: 14,
            family: 'Calibre-Light, Arial, sans-serif'
          },
          tickformat: ',',
          hoverformat: ',.0f',
          domain: [0, 1],
          anchor: 'y2',
          ...(chartXAxis === 'Year' && {
            tickmode: 'array',
            tickvals: tickvals,
            ticktext: ticktext,
            tickangle: 45,
            range: tickvals.length > 0 ? [Math.min(...tickvals) - 0.5, Math.max(...tickvals) + 0.5] : undefined
          })
        },
        yaxis2: {
          title: {
            text: '',
            font: {
              size: 18,
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            standoff: 30,
            automargin: true
          },
          tickfont: {
            size: 13,
            family: 'Calibre-Light, Arial, sans-serif'
          },
          // Use custom tick values and text for second subplot
          tickmode: customTickVals2.length > 0 ? 'array' : 'auto',
          tickvals: customTickVals2.length > 0 ? customTickVals2 : undefined,
          ticktext: customTickText2.length > 0 ? customTickText2 : undefined,
          hoverformat: ',.0f', // Keep full numbers in hover tooltips
          anchor: 'x2',
          domain: [0.05, 0.37], // Pushed down further, more space above title, smaller bottom chart
          side: 'left',
          tickangle: 0,
          automargin: true
        },
        hovermode: 'closest',
        showlegend: shouldShowLegend,
        // FIXED: Position legends side by side horizontally instead of stacked vertically
        legend: shouldShowLegend ? {
          x: 1.02,
          y: 0.95, // Top subplot area
          xanchor: 'left',
          yanchor: 'top',
          bgcolor: 'rgba(255,255,255,0.9)',
          bordercolor: '#E5E7EB',
          borderwidth: 1,
          font: {
            size: 11, // Readable font size
            family: 'Calibre-Light, Arial, sans-serif'
          },
          itemsizing: 'constant',
          itemwidth: 30,
          orientation: 'v',
          title: {
            text: selectedVariable || 'Variable 1',
            font: {
              size: 13,
              family: 'Calibre-Light, Arial, sans-serif',
              color: 'black'
            }
          },
          itemclick: 'toggle',
          itemdoubleclick: 'toggleothers'
        } : undefined,
        
        // Second legend positioned to the right of the first legend
        legend2: shouldShowLegend ? {
          x: 1.22, // Moved to the right of the first legend
          y: 0.95, // Same height as first legend
          xanchor: 'left',
          yanchor: 'top',
          bgcolor: 'rgba(255,255,255,0.9)',
          bordercolor: '#E5E7EB',
          borderwidth: 1,
          font: {
            size: 11, // Readable font size
            family: 'Calibre-Light, Arial, sans-serif'
          },
          itemsizing: 'constant',
          itemwidth: 30,
          orientation: 'v',
          title: {
            text: selectedVariable2 || 'Variable 2',
            font: {
              size: 13,
              family: 'Calibre-Light, Arial, sans-serif',
              color: 'black'
            }
          },
          itemclick: 'toggle',
          itemdoubleclick: 'toggleothers'
        } : undefined,
        margin: {
          l: 100,
          r: shouldShowLegend ? 280 : 20, // Much larger right margin for side-by-side legends
          t: 60,
          b: 100
        },
        autosize: false,
        font: {
          family: 'Calibre-Light, Arial, sans-serif',
          size: 14
        },
        hoverlabel: {
          bgcolor: 'white',
          bordercolor: 'black',
          font: {
            color: 'black',
            size: 12
          }
        }
      };

      const config = {
        responsive: false,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtons: [[]],
      };

      try {
        // Clear any existing plot first
        if (plotlyChartRef.current._fullLayout) {
          Plotly.purge(plotlyChartRef.current);
        }

        // CRITICAL: Use newPlot instead of react for initial render to ensure data integrity
        Plotly.newPlot(plotlyChartRef.current, plotlyDataArray, layout, config);
        console.log('Plotly comparative chart rendered successfully');
        
        // Verify data after render
        setTimeout(() => {
          if (plotlyChartRef.current && plotlyChartRef.current.data) {
            console.log('Post-render data verification:', {
              renderedTraces: plotlyChartRef.current.data.length,
              subplot1Traces: plotlyChartRef.current.data.filter(trace => !trace.yaxis || trace.yaxis === 'y').length,
              subplot2Traces: plotlyChartRef.current.data.filter(trace => trace.yaxis === 'y2').length,
              allTracesValid: plotlyChartRef.current.data.every(trace => trace.x && trace.x.length > 0)
            });
          }
        }, 100);
        
        // Force a resize after initial render to ensure proper fitting
        setTimeout(() => {
          if (plotlyChartRef.current && window.Plotly) {
            Plotly.Plots.resize(plotlyChartRef.current);
          }
        }, 200);
        
        // Add event listeners
        plotlyChartRef.current.on('plotly_hover', function(data) {
          console.log('Hover event detected:', data);
        });
        
        plotlyChartRef.current.on('plotly_unhover', function(data) {
          console.log('Unhover event detected:', data);
        });
        
      } catch (error) {
        console.error('Error rendering Plotly chart:', error);
      }
    });

    return () => {
      if (plotlyChartRef.current && window.Plotly) {
        try {
          if (plotlyChartRef.current._fullLayout) {
            Plotly.purge(plotlyChartRef.current);
          }
        } catch (error) {
          console.error('Error purging Plotly chart:', error);
        }
      }
    };
  }, [filteredData, filteredData2, selectedVariable, selectedMeasure, selectedVariable2, selectedMeasure2, chartXAxis, showLegend, chartDimensions]);

  // Handle window resize to update Plotly chart
  useEffect(() => {
    const handlePlotlyResize = debounce(() => {
      if (plotlyChartRef.current && window.Plotly && plotlyChartRef.current._fullLayout) {
        try {
          Plotly.Plots.resize(plotlyChartRef.current);
          console.log('ComparativeChartView: Plotly chart resized via window event');
        } catch (error) {
          console.warn('ComparativeChartView: Window resize failed (element may have been unmounted):', error);
        }
      }
    }, 200);

    window.addEventListener('resize', handlePlotlyResize);
    return () => window.removeEventListener('resize', handlePlotlyResize);
  }, []);

  // Check if we have any valid data to show
  const hasValidData = (filteredData && filteredData.length > 0) || (filteredData2 && filteredData2.length > 0);

  return (
    <>
      <style>{`
        .js-plotly-plot .plotly .hovertext {
          z-index: 10000 !important;
          pointer-events: none !important;
        }
        
        .plotly .hovertext {
          z-index: 10000 !important;
        }
        
        .js-plotly-plot .plotly .modebar {
          z-index: 1000 !important;
        }
        
        .plotly-chart .hovertext {
          background-color: rgba(31, 41, 55, 0.95) !important;
          color: white !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          border-radius: 4px !important;
          padding: 8px !important;
          font-size: 14px !important;
          font-family: Calibre-Light, Arial, sans-serif !important;
        }
      `}</style>
      <div 
        className="chart-view-container" 
        ref={chartContainerRef}
        style={{ 
          width: '100%',
          height: '100vh', // Full viewport height
          minHeight: '100vh', // Ensure minimum full height
          position: 'absolute', // Back to absolute positioning
          top: 0,
          left: 0, // Keep left at 0 but width will be constrained by parent
          right: 0,
          bottom: 0,
          padding: '5px',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <div 
          className="plotly-chart-container"
          style={{ 
            flex: '1',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '500px', // Increased minimum height
            position: 'relative'
          }}
        >
          {/* Custom download button - similar to ChartView */}
          {hasValidData && (
            <button
              onClick={downloadChartWithWatermark}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#374151',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontFamily: 'Calibre-Light, Arial, sans-serif',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(249, 250, 251, 1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
              }}
              title="Download comparative chart as PNG with watermark"
            >
              <i className="fas fa-download" style={{ fontSize: '10px' }}></i>
              PNG
            </button>
          )}

          {(!filteredData || filteredData.length === 0) && (!filteredData2 || filteredData2.length === 0) ? (
            <div className="no-data-message" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              textAlign: 'center',
              padding: '40px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontFamily: 'Calibre-Light, Arial, sans-serif'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '10px' }}>No data available for current selections.</p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>Please ensure both sets of filters have valid selections with available data.</p>
            </div>
          ) : (!filteredData || filteredData.length === 0) || (!filteredData2 || filteredData2.length === 0) ? (
            <div className="no-data-message" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              textAlign: 'center',
              padding: '40px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontFamily: 'Calibre-Light, Arial, sans-serif'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '10px' }}>One of the selections has no data.</p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>Check that both sets of filters have valid data for the selected countries and years.</p>
            </div>
          ) : (
            <div 
              ref={plotlyChartRef} 
              className="plotly-chart"
              style={{ 
                width: `${chartDimensions.width}px`, 
                height: `${chartDimensions.height}px`
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ComparativeChartView;